/* global globalThis */

import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "../component/pages/Falcon360/dashboard/dashboard.js";
import Header from "../component/header/Header";
import Sidebar from "../component/sidebar/Sidebar.js";
import Footer from "../component/footer/Footer.js";
import AuditLogsTable from "../component/common/audit/AuditLogs.js";
import ImportData from "../component/pages/Falcon360/upload/ImportData.js";
import ErrorPage from "../component/common/ErrorPage.js";
import DataSource from "../component/pages/Falcon360/dataSource/DataSource.js";
import ChatInterface from "../ai-agent/ui/ChatInterface.js";
import ChatModal from "../ai-agent/ui/ChatModal.js";
import DatabaseConnectionsTable from "../component/pages/Falcon360/dataSource/DataConnections.js";
import UploadedObjectsTable from "../component/pages/Falcon360/upload/UploadedObjectsTable.js";
import { LoaderCircle } from "lucide-react";
import axiosClient, { setAutoLogoutReset } from "../api-client/AxiosClient.js";
import FalconAIChatbot from "../ai-agent/ui/FalconAIChatbot.js";
import AIChatInterface from "../ai-agent/ui/aiagent.js";
import HelpPage from "../ai-agent/ui/help.js";
import GenerateScript from "../component/pages/Scriptly/GenerateScript.js";
import { useOrgConfigSync } from "../component/header/Orgconfigservice.js";
import { useMsal } from "@azure/msal-react";


const Layout = ({ isOpen, setIsOpen }) => {
  const { instance } = useMsal();
  const timerRef = useRef(null);
  const location = useLocation();

  useOrgConfigSync(); // Critical: Must be here to initiate permission sync before Header is rendered

  const logout = useCallback(() => {
    console.warn("Auto-logout triggered due to inactivity");
    sessionStorage.clear();
    localStorage.removeItem("organization_config_id");
    localStorage.removeItem("selectedCorporation");

    instance.logoutPopup().catch((e) => {
      console.error("Auto-logout failed:", e);
      globalThis.location.reload();
    });
  }, [instance]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    // 30 minute timeout
    timerRef.current = setTimeout(logout, 30 * 60 * 1000);
  }, [logout]);

  useEffect(() => {
    setAutoLogoutReset(resetTimer);
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "mousemove", "click"];
    activityEvents.forEach((event) => globalThis.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      activityEvents.forEach((event) => globalThis.removeEventListener(event, resetTimer));
    };
  }, [resetTimer]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [isRolesLoaded, setIsRolesLoaded] = useState(!localStorage.getItem("organization_config_id"));
  const [isUserGuideLoaded, setIsUserGuideLoaded] = useState(false);
  const [run, setRun] = useState(false);

  const isAgentRoute = location.pathname === "/ai-agent";
  const isViewDataRoute = location.pathname === "/view-data";
  const isHistoryPage = location.pathname.includes("/history-data");
  // Define valid paths for authenticated users
  const validPaths = [
    "/dashboard",
    "/upload",
    "/mapping",
    "/view-data",
    "/ai-agent",
    "/profile",
    "/falcon-ai",
    "/data-sources",
    "/data-sources/add-data-source",
    "/upload/add-upload",
    "/history-data",
    "/code-gen",
    "/scriptly",
    "/admin-interface",
    "/help",
    // Add all other valid authenticated routes here
  ];

  const fetchisUserGuideViewed = async () => {
    const response = await axiosClient.get("api/User/GuideViewStatus");
    setRun(response.data || false);
    setIsUserGuideLoaded(true);
  };

  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setIsRolesLoaded(true);
    };

    globalThis.addEventListener("permissionsUpdated", handlePermissionsUpdated);

    // Initial check: if roles are already in session storage, unlock immediately
    if (sessionStorage.getItem("rolesAndPermission")) {
      setIsRolesLoaded(true);
    }

    return () => {
      globalThis.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
    };
  }, []);

  useEffect(() => {
    fetchisUserGuideViewed();
  }, []);

  // Check if current path is valid
  const isValidPath =
    validPaths.includes(location.pathname) ||
    location.pathname.startsWith("/history-data/");

  // If path is invalid, redirect to dashboard
  if (!isValidPath && location.pathname !== "/") {
    return <Navigate to="/dashboard" replace />;
  }

  const closeChat = () => setChatOpen(false);
  const toggleMinimize = () => setChatMinimized((prev) => !prev);

  const isScriptlyRoute = location.pathname === "/scriptly";

  // Calculate content width based on route and sidebar state
  const getContentStyle = () => {
    let marginLeft = "0px";
    if (!isAgentRoute) {
      marginLeft = isOpen ? "0px" : "90px";
    }

    let padding = "0px";
    if (!isAgentRoute && !isScriptlyRoute) {
      padding = "10px";
    }

    const baseStyle = {
      marginLeft,
      marginTop: "80px", // Add margin-top to account for fixed header
      padding,
      width: "100%",
      transition: "all 0.3s ease-in-out",
      height: isScriptlyRoute ? "calc(100vh - 80px)" : "auto",
      overflow: isScriptlyRoute ? "hidden" : "visible",
    };

    if (isHistoryPage) {
      return {
        ...baseStyle,
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "100px",
      };
    }

    // Special handling for view-data route (CreateTable component)
    if (isViewDataRoute) {
      return {
        ...baseStyle,
        maxWidth: isOpen ? "calc(100vw)" : "calc(100vw - 100px)",
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "100px",
      };
    }

    return baseStyle;
  };

  return (
    <>
      {isRolesLoaded && isUserGuideLoaded ? (
        <div
          className="app-wrapper"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setRun={setRun}
            run={run}
          />

          <div
            className={`app-container ${isAgentRoute ? "h-screen" : ""}`}
            style={{ display: "flex", flex: 1 }}
          >
            {!isAgentRoute && <Sidebar isOpen={isOpen} />}

            <div className="content" style={getContentStyle()}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/data-sources"
                  element={<DatabaseConnectionsTable />}
                />
                <Route path="/upload" element={<UploadedObjectsTable />} />
                <Route path="/logs" element={<AuditLogsTable />} />
                <Route path="/error" element={<ErrorPage />} />
                <Route path="/ai-agent" element={<ChatInterface />} />
                <Route path="/falcon-ai" element={<FalconAIChatbot />} />
                <Route path="/code-gen" element={<AIChatInterface />} />

                <Route path="/help" element={<HelpPage />} />
                <Route
                  path="/data-sources/add-data-source"
                  element={<DataSource />}
                />
                <Route path="/upload/add-upload" element={<ImportData />} />
                <Route path="/scriptly" element={<GenerateScript />} />
                {/* Catch all invalid paths and redirect to dashboard */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </div>

            {/* Chat Modal */}
            <ChatModal
              open={chatOpen}
              minimized={chatMinimized}
              onClose={closeChat}
              onMinimize={toggleMinimize}
            >
              <ChatInterface />
            </ChatModal>
          </div>

          <Footer />
        </div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <LoaderCircle className="animate-spin text-blue-900" size={50} />
        </div>
      )}
    </>
  );
};

Layout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default Layout;
